import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { taskKeys, useTaskCreateQuery, useTaskUpdateQuery } from 'hooks/api';
import { useAppLocation } from 'hooks/helpers';
import { useModal } from 'hooks/ui';
import { taskRoutes } from 'router/taskRoutes';
import { useAISuggestionStore } from 'store/ai-suggestion';
import { resetDraft, setTasks, taskTemplate, useTaskStore } from 'store/task';
import { useLeaveAIAssistant } from './useLeaveAIAssistant';
import { AllStatuses } from 'interfaces';

type HookArgs = {
  onSuccess: () => void;
  onAbort: () => void;
};

export const useTaskSwitcher = () => {
  const { isCreate } = useAppLocation(taskRoutes);
  const queryClient = useQueryClient();
  const modal = useModal();
  const { errors, draft: draftTask, assignedGroup } = useTaskStore();
  const activeAISuggestion = useAISuggestionStore(
    (state) => state.activeAISuggestion,
  );
  const { mutate: createTask } = useTaskCreateQuery();
  const { mutate: updateTask } = useTaskUpdateQuery();
  const isFormDraftable = !draftTask?.title.trim().length;
  const leaveAIAssistant = useLeaveAIAssistant();

  const hasErrors = useMemo(
    () => Object.values(errors).some((field) => field.length > 0),
    [errors],
  );

  const promptToUpdate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: () => {
        updateTask({ task: draftTask, assignedGroup });

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();
        setTasks(queryClient.getQueryData(taskKeys.all) ?? []);

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  const promptToCreate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: (): void => {
        createTask(draftTask);

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  return ({ onSuccess, onAbort }: HookArgs) => {
    const isDraftDirty = draftTask?.status === AllStatuses.NOT_SAVED;
    const isDraftEmpty =
      JSON.stringify(draftTask) === JSON.stringify(taskTemplate);

    if (isDraftDirty && (!isDraftEmpty || isCreate)) {
      if (!draftTask?.id) {
        return promptToCreate({ onSuccess, onAbort });
      }

      return promptToUpdate({ onSuccess, onAbort });
    }

    if (activeAISuggestion) {
      return leaveAIAssistant({
        onSuccess,
        onAbort: () => {},
      });
    }

    return onSuccess();
  };
};
